import { useEffect, useState } from 'react'
import { Bell } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { notificationsApi, type UserNotification } from '@/api/notifications'
import { formatFeedbackDateTime } from '@/shared/components/support/feedback-ui'
import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { notifyApiError } from '@/shared/lib/notify'

import { useNotificationPreview, useUnreadNotificationCount } from '../hooks/useNotifications'

function previewBody(body: string): string {
  const trimmed = body.trim()
  if (trimmed.length <= 72) return trimmed
  return `${trimmed.slice(0, 72)}…`
}

export function NotificationBell() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { count, refresh: refreshCount } = useUnreadNotificationCount()
  const { items, loading, load } = useNotificationPreview()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (open) void load()
  }, [open, load])

  const handleOpenItem = async (item: UserNotification) => {
    try {
      if (!item.read) {
        await notificationsApi.markRead(item.public_id, true)
        void refreshCount()
      }
    } catch (err) {
      notifyApiError(err, { fallback: t('messages.markReadFailed') })
    }
    setOpen(false)
    if (item.link_path.startsWith('http')) {
      window.location.assign(item.link_path)
      return
    }
    navigate(item.link_path)
  }

  const badge =
    count > 0 ? (count > 99 ? '99+' : String(count)) : null

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="portal-notification-bell relative h-9 w-9 rounded-full text-zinc-300 hover:bg-white/5 hover:text-zinc-100"
          aria-label={t('messages.bellLabel')}
        >
          <Bell size={18} aria-hidden />
          {badge ? (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-indigo-500 px-1 text-[10px] font-semibold leading-none text-white">
              {badge}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[min(24rem,calc(100vw-2rem))]">
        <DropdownMenuLabel className="flex items-center justify-between gap-2 px-3 py-2">
          <span>{t('messages.title')}</span>
          {count > 0 ? (
            <span className="text-[11px] font-normal text-zinc-500">
              {t('messages.unreadCount', { count })}
            </span>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {loading ? (
          <p className="px-3 py-4 text-sm text-zinc-500">{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <p className="px-3 py-4 text-sm text-zinc-500">{t('messages.empty')}</p>
        ) : (
          items.map((item) => (
            <DropdownMenuItem
              key={item.public_id}
              className={clsx(
                'flex cursor-pointer flex-col items-start gap-1 px-3 py-2.5',
                !item.read && 'bg-indigo-500/5',
              )}
              onSelect={(event) => {
                event.preventDefault()
                void handleOpenItem(item)
              }}
            >
              <span className="line-clamp-1 text-sm font-medium text-zinc-100">{item.title}</span>
              {item.body ? (
                <span className="line-clamp-2 text-xs leading-relaxed text-zinc-500">
                  {previewBody(item.body)}
                </span>
              ) : null}
              <span className="text-[10px] text-zinc-600">
                {formatFeedbackDateTime(item.created_at)}
              </span>
            </DropdownMenuItem>
          ))
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="justify-center text-indigo-300">
          <Link to="/messages">{t('messages.viewAll')}</Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
